/**
 * Odds Movement Tracker for Major Leagues
 * Monitors betting line changes and stores historical odds data
 */

import { ref, set, get, push, onValue, serverTimestamp } from 'firebase/database';
import { rtdb, logCache } from './rtdbConfig';
import { MAJOR_LEAGUE_IDS, LIVE_ODDS_PRIORITY_LEAGUES } from '../../../../components/dashboards/utils/majorLeagues';

class OddsTracker {
  constructor() {
    this.trackedFixtures = new Map();
    this.listeners = new Map();
    this.movementThreshold = 0.15; // 15% movement triggers alert
  }

  /**
   * Start tracking odds for a fixture
   */
  async trackFixture(fixtureId, leagueId) {
    if (this.trackedFixtures.has(fixtureId)) {
      return; // Already tracking
    }

    try {
      // Only track major league games
      if (!MAJOR_LEAGUE_IDS.includes(Number(leagueId))) {
        return;
      }

      logCache('info', `Started tracking odds for fixture ${fixtureId}`);
      
      // Set up real-time listener for odds changes
      const oddsRef = ref(rtdb, `cache/odds/prematch/${fixtureId}`);
      const listener = onValue(oddsRef, async (snapshot) => {
        if (snapshot.exists()) {
          const currentOdds = snapshot.val();
          await this.processOddsMovement(fixtureId, currentOdds);
        }
      });

      this.listeners.set(fixtureId, listener);
      this.trackedFixtures.set(fixtureId, {
        leagueId,
        startTime: Date.now(),
        movements: []
      });

    } catch (error) {
      logCache('error', `Failed to track fixture ${fixtureId}`, error);
    }
  }

  /**
   * Process odds movement and store history
   */
  async processOddsMovement(fixtureId, currentOdds) {
    try {
      const historyRef = ref(rtdb, `odds_history/${fixtureId}`);
      const historySnapshot = await get(historyRef);
      
      let history = [];
      if (historySnapshot.exists()) {
        history = Object.values(historySnapshot.val());
      }

      // Extract key odds (H2H market)
      const h2hOdds = this.extractH2HOdds(currentOdds.data);
      
      if (h2hOdds) {
        // Check for significant movement
        if (history.length > 0) {
          const lastOdds = history[history.length - 1];
          const movement = this.calculateMovement(lastOdds, h2hOdds);
          
          if (movement.significant) {
            await this.recordMovement(fixtureId, movement);
            this.notifyMovement(fixtureId, movement);
          }
        }

        // Store in history
        const historyEntry = {
          timestamp: Date.now(),
          ...h2hOdds,
          bookmakers: h2hOdds.bookmakers?.length || 0
        };

        // Push to history (keep last 50 entries)
        await push(ref(rtdb, `odds_history/${fixtureId}`), historyEntry);
        
        // Clean old entries if needed
        if (history.length > 50) {
          await this.cleanOldHistory(fixtureId, history);
        }
      }
    } catch (error) {
      logCache('error', `Failed to process odds movement for ${fixtureId}`, error);
    }
  }

  /**
   * Extract H2H odds from odds data
   */
  extractH2HOdds(oddsData) {
    if (!oddsData || !Array.isArray(oddsData) || oddsData.length === 0) {
      return null;
    }

    const firstOdds = oddsData[0];
    const bookmakers = firstOdds.bookmakers || [];
    
    // Aggregate odds from multiple bookmakers
    const aggregated = {
      home: [],
      draw: [],
      away: []
    };

    bookmakers.forEach(bookmaker => {
      const h2hBet = bookmaker.bets?.find(bet => bet.id === 1); // H2H market
      if (h2hBet && h2hBet.values) {
        h2hBet.values.forEach(value => {
          if (value.value === 'Home') aggregated.home.push(parseFloat(value.odd));
          if (value.value === 'Draw') aggregated.draw.push(parseFloat(value.odd));
          if (value.value === 'Away') aggregated.away.push(parseFloat(value.odd));
        });
      }
    });

    // Calculate averages
    const average = (arr) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
    
    return {
      homeOdds: average(aggregated.home),
      drawOdds: average(aggregated.draw),
      awayOdds: average(aggregated.away),
      bookmakers: bookmakers.map(b => b.name),
      sampleSize: bookmakers.length
    };
  }

  /**
   * Calculate odds movement
   */
  calculateMovement(oldOdds, newOdds) {
    const movements = {
      home: this.calculatePercentChange(oldOdds.homeOdds, newOdds.homeOdds),
      draw: this.calculatePercentChange(oldOdds.drawOdds, newOdds.drawOdds),
      away: this.calculatePercentChange(oldOdds.awayOdds, newOdds.awayOdds)
    };

    const maxMovement = Math.max(
      Math.abs(movements.home),
      Math.abs(movements.draw),
      Math.abs(movements.away)
    );

    return {
      ...movements,
      significant: maxMovement >= this.movementThreshold,
      maxMovement,
      timestamp: Date.now(),
      oldOdds,
      newOdds
    };
  }

  /**
   * Calculate percent change
   */
  calculatePercentChange(oldValue, newValue) {
    if (!oldValue || !newValue) return 0;
    return ((newValue - oldValue) / oldValue) * 100;
  }

  /**
   * Record significant movement
   */
  async recordMovement(fixtureId, movement) {
    try {
      const movementRef = ref(rtdb, `odds_movements/${fixtureId}/${Date.now()}`);
      await set(movementRef, {
        ...movement,
        recorded: serverTimestamp()
      });
      
      logCache('info', `Recorded significant odds movement for fixture ${fixtureId}`, movement);
    } catch (error) {
      logCache('error', `Failed to record movement for ${fixtureId}`, error);
    }
  }

  /**
   * Notify about significant movement
   */
  notifyMovement(fixtureId, movement) {
    // Dispatch custom event for UI components to listen to
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('oddsMovement', {
        detail: {
          fixtureId,
          movement,
          timestamp: Date.now()
        }
      }));
    }

    logCache('info', `🚨 Significant odds movement detected for fixture ${fixtureId}!`, {
      home: `${movement.home > 0 ? '+' : ''}${movement.home.toFixed(1)}%`,
      draw: `${movement.draw > 0 ? '+' : ''}${movement.draw.toFixed(1)}%`,
      away: `${movement.away > 0 ? '+' : ''}${movement.away.toFixed(1)}%`
    });
  }

  /**
   * Clean old history entries
   */
  async cleanOldHistory(fixtureId, history) {
    try {
      // Keep only last 50 entries
      const sorted = history.sort((a, b) => a.timestamp - b.timestamp);
      const toDelete = sorted.slice(0, sorted.length - 50);
      
      // Delete old entries
      for (const entry of toDelete) {
        const entryRef = ref(rtdb, `odds_history/${fixtureId}/${entry.key}`);
        await set(entryRef, null);
      }
    } catch (error) {
      logCache('error', `Failed to clean history for ${fixtureId}`, error);
    }
  }

  /**
   * Get odds history for a fixture
   */
  async getOddsHistory(fixtureId) {
    try {
      const historyRef = ref(rtdb, `odds_history/${fixtureId}`);
      const snapshot = await get(historyRef);
      
      if (!snapshot.exists()) {
        return [];
      }

      const history = Object.entries(snapshot.val()).map(([key, value]) => ({
        ...value,
        key
      }));

      return history.sort((a, b) => a.timestamp - b.timestamp);
    } catch (error) {
      logCache('error', `Failed to get history for ${fixtureId}`, error);
      return [];
    }
  }

  /**
   * Get significant movements for a fixture
   */
  async getMovements(fixtureId) {
    try {
      const movementsRef = ref(rtdb, `odds_movements/${fixtureId}`);
      const snapshot = await get(movementsRef);
      
      if (!snapshot.exists()) {
        return [];
      }

      return Object.values(snapshot.val()).sort((a, b) => b.timestamp - a.timestamp);
    } catch (error) {
      logCache('error', `Failed to get movements for ${fixtureId}`, error);
      return [];
    }
  }

  /**
   * Stop tracking a fixture
   */
  stopTracking(fixtureId) {
    const listener = this.listeners.get(fixtureId);
    if (listener) {
      listener(); // Unsubscribe
      this.listeners.delete(fixtureId);
      this.trackedFixtures.delete(fixtureId);
      logCache('info', `Stopped tracking odds for fixture ${fixtureId}`);
    }
  }

  /**
   * Start auto-tracking for today's major league games
   */
  async autoTrackMajorGames(fixtures) {
    const majorGames = fixtures.filter(f => 
      LIVE_ODDS_PRIORITY_LEAGUES.includes(f.league?.id)
    );

    for (const game of majorGames) {
      await this.trackFixture(game.fixture.id, game.league.id);
    }

    logCache('info', `Auto-tracking ${majorGames.length} major league games`);
  }

  /**
   * Get tracking status
   */
  getStatus() {
    return {
      tracking: this.trackedFixtures.size,
      fixtures: Array.from(this.trackedFixtures.keys())
    };
  }
}

// Create singleton instance
const oddsTracker = new OddsTracker();

// Export for browser console debugging
if (typeof window !== 'undefined') {
  window.RTDBOddsTracker = {
    track: (fixtureId, leagueId) => oddsTracker.trackFixture(fixtureId, leagueId),
    history: (fixtureId) => oddsTracker.getOddsHistory(fixtureId),
    movements: (fixtureId) => oddsTracker.getMovements(fixtureId),
    status: () => oddsTracker.getStatus(),
    stopTracking: (fixtureId) => oddsTracker.stopTracking(fixtureId),
    extractOdds: (oddsData) => oddsTracker.extractH2HOdds(oddsData)
  };
}

// Export both the instance and the extraction method
export { oddsTracker as default };
export const extractH2HOdds = (oddsData) => oddsTracker.extractH2HOdds(oddsData);